import { LessThemeProvider, useLessTheme } from '@threatadvisor/react-utils';
import { theme } from 'antd';
import {
  ThemeProvider as AntdThemeProvider,
  ThemeMode,
  createGlobalStyle,
  useTheme as useAntdTheme,
  useThemeMode
} from 'antd-style';
import React, { ReactElement, useEffect } from 'react';

// DirectDefense Branding Colors
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ddbrand: any = {
  primaryNeutralBlack: '#232121',
  primaryNeutralBlack65: '#706f6f',
  primaryNeutralBlack85: '#444242',
  primaryCoolGray: '#c8c9c7',
  primaryCoolGray65: '#dbdcdb',
  primaryCoolGray100: '#ffffff',
  secondaryGrayOne: '#c8c9c7',
  secondaryGrayTwo: '#c8c9c7',
  secondaryGrayThree: '#54585a',
  secondaryBlue: '#009cde',
  secondaryOrange: '#fa4616',
  tertiaryLightBlue: '#8dc9e8',
  tertiaryDarkBlue: '#00539f',
  tertiaryYellow: '#ffbb02',
  tertiaryGreen: '#0bb27c'
};

const Global = createGlobalStyle({
  body: {
    margin: 0
  },
  ':root': {
    '& .ant-empty': {
      margin: 'auto'
    },
    '& .pageContainer': {
      height: '100%',
      width: '100%',
      padding: '15px',
      overflow: 'auto'
    },
    "& [data-theme='light'] .pageContainer": { backgroundColor: 'white' },
    '& .ant-tabs': {
      minHeight: '0'
    },
    '& .ant-tabs-content-holder': {
      display: 'flex',
      minHeight: '0'
    },
    '& .ant-tabs-content': {
      display: 'flex',
      minHeight: '0'
    },
    '& .ant-tabs-tabpane-active': {
      display: 'flex',
      minHeight: '0',
      minWidth: '0',
      flex: 1
    },
    '& .ant-tabs-tabpane > div': {
      flex: 1
    },
    '& .ant-typography': {
      wordBreak: 'normal'
    },
    "& [data-theme='dark'] .ant-notification-notice": {
      boxShadow:
        '0 6px 8px 0 rgba(255, 255, 255, 0.02), 0 3px 3px -4px rgba(255, 255, 255, 0.5), 0 9px 10px 8px rgba(255, 255, 255, 0.35)',
      borderRadius: '2px',
      borderColor: 'rgba(255, 255, 255, 0.25)'
    },
    "& [data-theme='light'] .ant-notification-notice": {
      boxShadow:
        '0 6px 8px 0 rgba(0, 0, 0, 0.02), 0 3px 3px -4px rgba(0, 0, 0, 0.5), 0 9px 10px 8px rgba(0, 0, 0, 0.35)',
      borderRadius: '2px',
      borderColor: 'rgba(0, 0, 0, 0.25)'
    }
  }
});

export function useTheme(): {
  toggleTheme: () => void;
  setAppearance: (appearance: 'light' | 'dark') => void;
  isDarkMode: boolean;
  appearance: 'light' | 'dark' | string;
} {
  const { setAppearance, isDarkMode, appearance } = useThemeMode();

  const toggleTheme = () => {
    if (appearance === 'dark') {
      applyLightScrollbarTheme();
    } else {
      applyDarkScrollbarTheme();
    }
    setAppearance(appearance === 'light' ? 'dark' : 'light');
  };

  return { toggleTheme, setAppearance, isDarkMode, appearance };
}

export function applyDarkScrollbarTheme() {
  const root = document.documentElement;
  root.style.setProperty('--scrollbar-track', '#131C3B');
  root.style.setProperty('--scrollbar-thumb', '#58AEFE');
  root.style.setProperty('--scrollbar-thumb-hover', '#555');
}

export function applyLightScrollbarTheme() {
  const root = document.documentElement;
  root.style.setProperty('--scrollbar-track', '#FFF');
  root.style.setProperty('--scrollbar-thumb', '#000');
  root.style.setProperty('--scrollbar-thumb-hover', '#555');
}
let defaultAppearanceString = 'light';

// Only take the time to check local storage and media if this is not a report
// On report page, always use light theme
if (!window.location.pathname.startsWith('/workbench/report')) {
  // Get the user's OS preference
  const osTheme =
    window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';

  defaultAppearanceString = window.localStorage.getItem('theme') || osTheme;
  if (defaultAppearanceString === 'light') {
    applyLightScrollbarTheme();
  } else {
    applyDarkScrollbarTheme();
  }
}

export function ThemeProvider({
  basename,
  children
}: {
  basename: string;
  children: ReactElement;
}): ReactElement {
  const { defaultAlgorithm, darkAlgorithm } = theme;

  useEffect(() => {}, []);
  return (
    <AntdThemeProvider
      onAppearanceChange={appearance => {
        window.localStorage.setItem('theme', appearance);
      }}
      defaultThemeMode={defaultAppearanceString as ThemeMode}
      theme={appearance => ({
        cssVar: { key: 'default' },
        hashed: false,
        algorithm: appearance === 'dark' ? darkAlgorithm : defaultAlgorithm,
        token: {
          borderRadius: 2,
          ...(appearance === 'dark'
            ? {
                colorPrimary: '#009cde',
                colorError: '#fa4616'
              }
            : {})
        },
        components: {
          Menu: {
            itemBg: '--layout-body-background',
            algorithm: true
          },
          Layout: {
            siderBg: appearance === 'dark' ? 'black' : 'white',
            headerBg: appearance === 'dark' ? 'black' : 'white',
            triggerBg: '--layout-body-background',
            triggerColor: appearance === 'dark' ? 'white' : 'black',
            height: '50vh',
            algorithm: true
          },
          Tabs: {
            cardBg: appearance === 'dark' ? ddbrand.primaryNeutralBlack : ddbrand.primaryCoolGray65
          }
        }
      })}
    >
      <Global />
      <LessThemeProvider defaultTheme={defaultAppearanceString}>
        <LessThemeContainer>{children}</LessThemeContainer>
      </LessThemeProvider>
    </AntdThemeProvider>
  );
}

function LessThemeContainer({ children }: { children: ReactElement }): ReactElement {
  const { appearance } = useAntdTheme();
  const [_currentTheme, setMode, _status] = useLessTheme();
  useEffect(() => {
    setMode(appearance);
  }, [appearance]);

  return <>{children}</>;
}
